import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { scrollToTop } from "../../Global/Scroll";
import { errorMessages } from "../../../Constants/Errors";
import { blog_page_labels } from "../../../Constants/Labels";
import { alert_message_type } from "../../../Constants/Messages";
import { callAPI, useStore, usePage, useUser } from "../../../Utils/utils";
import Message from "../../Message/Message";
import Alert from "../../Alert/Alert";
import { PopUpMessage } from "../../Global/PopupMessage/PopupMessage";
import ImageSelector from "../../ImageSelector/BlogImageSelector";
import ImagesPreview from "../../ImagesPreview/BlogImagesPreview";
import ErrorMessage from "../../Global/ErrorMessage";
import TextBlock from "./TextBlock";
import ImageBlock from "./ImageBlock";
import QuoteBlock from "./QuoteBlock";
import "./BlogEdit.css";
import "./BlogContentEditor.css";
import "./DragDrop.css";

export default function BlogEdit({ onUpdateBlog }) {
  var { id } = useParams();
  const navigate = useNavigate();
  const { setPage } = usePage();
  const { user } = useUser();
  const { store } = useStore();
  const [errors, setErrors] = useState();
  const [alert, setAlert] = useState();
  const [loading, setLoading] = useState(false);
  const [formValues, setformValues] = useState({});
  const [savedImages, setSavedImages] = useState([]);
  const [imageSelector, setImageSelector] = useState();
  const [selectedImages, setSelectedImages] = useState({});
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [openDelectPopup, setOpenDelectPopup] = useState();
  const [categories, setCategories] = useState([]);
  const [contentBlocks, setContentBlocks] = useState([]);
  const [showBlockOptions, setShowBlockOptions] = useState(false);
  const [imageSelectionContext, setImageSelectionContext] = useState(-1);
  const [focusBlockIndex, setFocusBlockIndex] = useState(null);

  useEffect(() => {
    setPage({ isLoading: loading });
  }, [loading]);

  const onChangeField = (event) => {
    const { name, value, type, checked } = event.target;
    const fieldValue = type === "checkbox" ? checked : value;
    setformValues({ ...formValues, [name]: fieldValue });
    setShowErrorMessage(false);
    setErrors([]);
  };

  useEffect(() => {
    if (id && store?.id) {
      callAPI(
        `/admin/blog/${id}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            token: user?.auth_token,
          },
        },
        navigate,
        setLoading,
      ).then((data) => {
        if (data.success === true) {
          setformValues(data.record || {});
          if (data?.record?.featured_image_data) {
            setSavedImages([
              {
                blog_image_id: data?.record?.featured_image_data?.blog_image_id,
                path: data?.record?.featured_image_data?.path,
                name: data?.record?.featured_image_data?.name,
              },
            ]);
            setSelectedImages(
              {
                blog_image_id: data?.record?.featured_image_data?.blog_image_id,
                path: data?.record?.featured_image_data?.path,
                name: data?.record?.featured_image_data?.name,
              }
            );
          }
          if (data?.record?.content) {
            try {
              const parsedContent = data?.record?.content;
              if (parsedContent) {
                setContentBlocks(parsedContent);
                // // Update nextBlockId to avoid conflicts
                // const maxId = Math.max(
                //   ...parsedContent.map((block) => block.id),
                // );
                // setNextBlockId(maxId + 1);
              }
            } catch (e) {
              console.error("Error parsing content:", e);
            }
          }
        }
      });
    }
  }, [id, store?.id]);

  useEffect(() => {
    if (store?.id) {
      callAPI(
        "/admin/blog-categories/by-store",
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            token: user?.auth_token,
          },
          body: JSON.stringify({
            store_id: store?.id,
            status: "all",
            page_size: 100,
            current_page: 1,
          }),
        },
        navigate,
        setLoading,
      ).then((data) => {
        if (data.success) {
          setCategories(data.records);
        }
      });
    }
  }, [store?.id]);

  const saveBlog = () => {
    scrollToTop();

    // Validate mandatory fields
    const validationErrors = {};
    
    if (!formValues?.title?.trim()) {
      validationErrors.title = "Title is required";
    }
    
    if (!formValues?.sub_title?.trim()) {
      validationErrors.sub_title = "Subtitle is required";
    }
    
    if (!formValues?.blog_category_id) {
      validationErrors.blog_category_id = "Category is required";
    }
    
    if (!formValues?.status) {
      validationErrors.status = "Status is required";
    }

    if(contentBlocks.length === 0) {
      validationErrors.content = "Content is required";
    }

     if (!selectedImages?.blog_image_id ) {
      validationErrors.featured_image = "Featured image is required";
    }

    // If there are validation errors, show them and stop
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setShowErrorMessage(true);
      scrollToTop();
      return;
    }

    // Clear any previous errors
    setErrors({});
    setShowErrorMessage(false);

    const payload = {
      blog_id: id,
      title: formValues?.title?.trim(),
      sub_title: formValues?.sub_title?.trim(),
      content: JSON.stringify(contentBlocks),
      blog_category_id: formValues?.blog_category_id,
      status: formValues?.status,
    };

   if (selectedImages?.blog_image_id) {
      payload.featured_image = selectedImages?.blog_image_id;
    }

    callAPI(
      "/admin/blog/update",
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          token: user?.auth_token,
        },
        body: JSON.stringify(payload),
      },
      navigate,
      setLoading,
    ).then((data) => {
      if (data.success === true) {
        onUpdateBlog(alert_message_type.success);
      } else {
        setErrors(data?.errors);
        setShowErrorMessage(true);
        onUpdateBlog(alert_message_type.error);
      }
    });
  };

  const onOpenFeaturedImage = (configData) => {
    setImageSelectionContext(-1);
    setImageSelector(configData);
  };

  const onOpenContentBlockImage = (configData, blockIndex) => {
    setImageSelectionContext(blockIndex);
    setImageSelector(configData);
  };

  const onClose = () => {
    setImageSelector(false);
    setImageSelectionContext(-1);
  };

  const onSelectImages = (images) => {
    if (imageSelectionContext !== -1) {
      updateContentBlock(imageSelectionContext, images);
      // setImageSelectionContext(null);
    } else {
      // This is for featured image selection
      setSelectedImages(images);
    }
  };

  const onRemoveImageIds = () => {
    setSelectedImages(null);
  };

  const addContentBlock = (type) => {
    setContentBlocks((prevBlocks) => {
      const newBlocks = [
        ...prevBlocks,
        {
          type: type,
          content: type === "text" ? "" : type === "quote" ? "" : null,
        },
      ];

      // focus last block
      setFocusBlockIndex(newBlocks.length - 1);

      return newBlocks;
    });
  };

  const removeContentBlock = (blockId) => {
    // if (contentBlocks.length > 1) {
    setContentBlocks(contentBlocks.filter((block, index) => index !== blockId));
    // }
  };

  // const updateContentBlock = (blockId, content) => {
  //   setContentBlocks(
  //     contentBlocks.map((block, index) =>
  //       index === blockId ? { ...block, content } : block,
  //     ),
  //   );
  // };

  const updateContentBlock = (blockId, content) => {
    setContentBlocks((prevBlocks) =>
      prevBlocks.map((block, index) =>
        index === blockId ? { ...block, content } : block,
      ),
    );
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", index);
    e.target.style.opacity = "0.5";
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "#f8f9fa";
  };

  const handleDragLeave = (e) => {
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";

    const dragIndex = parseInt(e.dataTransfer.getData("text/plain"));

    if (dragIndex === dropIndex) return;

    const draggedBlock = contentBlocks[dragIndex];
    const newBlocks = [...contentBlocks];

    // Remove dragged block from its original position
    newBlocks.splice(dragIndex, 1);

    // Insert dragged block at the new position
    newBlocks.splice(dropIndex, 0, draggedBlock);

    setContentBlocks(newBlocks);
  };

  const moveBlockUp = (index) => {
    if (index > 0) {
      const newBlocks = [...contentBlocks];
      [newBlocks[index], newBlocks[index - 1]] = [
        newBlocks[index - 1],
        newBlocks[index],
      ];
      setContentBlocks(newBlocks);
    }
  };

  const moveBlockDown = (index) => {
    if (index < contentBlocks.length - 1) {
      const newBlocks = [...contentBlocks];
      [newBlocks[index], newBlocks[index + 1]] = [
        newBlocks[index + 1],
        newBlocks[index],
      ];
      setContentBlocks(newBlocks);
    }
  };

  const statusOptions = Object.keys(
    blog_page_labels?.blog_add_and_edit_page_status,
  )?.map((name) => {
    const value = blog_page_labels?.blog_add_and_edit_page_status[name];
    return `${name},${value}`;
  });

  const onOpenMessagePopUp = () => {
    setOpenDelectPopup(true);
  };

  const onDelete = () => {
    callAPI(
      `/admin/blog/${id}/delete`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          token: user?.auth_token,
        },
      },
      navigate,
      setLoading,
    ).then((data) => {
      if (data.success) {
        navigate("/admin/blogs");
        localStorage.setItem("Message", "Blog deleted successfully");
      } else {
        setAlert({
          text: "Error deleting blog",
          type: alert_message_type.error,
        });
        setOpenDelectPopup(false);
      }
    });
  };

  const onCloseMessagePopUp = () => {
    setOpenDelectPopup(false);
  };

  return (
    <React.Fragment>
      <button className="btn btn-primary saveButton mb-4" onClick={saveBlog}>
        {blog_page_labels?.blog_edit_page_labels?.save_btn_label}
      </button>
      <button
        className="btn delectButton mb-4 mx-3"
        onClick={() => onOpenMessagePopUp()}
      >
        {blog_page_labels?.blog_edit_page_labels?.delete_btn_label}
      </button>
      {openDelectPopup && (
        <PopUpMessage
          delectMessage={true}
          isDelect={true}
          onDelete={onDelete}
          onClose={onCloseMessagePopUp}
          recipientsCount={true}
        />
      )}

      {alert?.text && (
        <div className="text-center">
          <Alert text={alert.text} type={alert.type} setAlert={setAlert} />
        </div>
      )}

      <div className="row">
        <div className="col-lg-8">
          <div className="card card-default">
            <div className="card-header card-header-border-bottom">
              <h2>{blog_page_labels?.blog_edit_page_labels?.title}</h2>
            </div>

            <div className="card-body" style={{ overflow: "visible" }}>
              <div className="ec-store-uploads">
                <div className="form-group">
                  <label className="form-label mandatory_field">
                    {blog_page_labels?.blog_add_and_edit_page_labels?.title}
                  </label>
                  <textarea
                    className="form-control"
                    maxLength="200"
                    onChange={onChangeField}
                    name="title"
                    value={formValues?.title || ""}
                  />
                  <div className="errorMessage">
                    <Message
                      text={
                        errorMessages[
                          ErrorMessage({ fieldName: "title", errors })
                        ] ||  ErrorMessage({ fieldName: "title", errors })
                      }
                      type={"errorMsg"}
                      showMessage={showErrorMessage}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label mandatory_field">
                    {blog_page_labels?.blog_add_and_edit_page_labels
                      ?.sub_title || "Sub Title"}
                  </label>
                  <textarea
                    className="form-control"
                    maxLength="300"
                    onChange={onChangeField}
                    name="sub_title"
                    value={formValues?.sub_title || ""}
                    placeholder="Enter sub title"
                  />
                  <div className="errorMessage">
                    <Message
                      text={
                        errorMessages[
                          ErrorMessage({ fieldName: "sub_title", errors })
                        ] || ErrorMessage({ fieldName: "sub_title", errors })
                      }
                      type={"errorMsg"}
                      showMessage={showErrorMessage}
                    />
                  </div>
                </div>
                <div className="form-group mb-3">
                  <label className="form-label mandatory_field">
                    {blog_page_labels?.blog_add_and_edit_page_labels?.content}
                  </label>
                  {/* <div className="content-editor"> */}

                  {/* <div className="content-blocks-list"> */}
                  {contentBlocks.map((block, index) => (
                    <div
                      key={index}
                      className="content-block-content"
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      style={{ cursor: "move" }}
                    >
                      <div className="drag-handle">
                        <span className="mdi mdi-drag-horizontal"></span>
                      </div>
                      {block.type === "text" && (
                        <TextBlock
                          text={block.content}
                          onChangeText={(value) =>
                            updateContentBlock(index, value)
                          }
                          onRemove={() => {
                            removeContentBlock(index);
                          }}
                        />
                      )}

                      {block.type === "quote" && (
                        <QuoteBlock
                          quoteBlock={block.content}
                          onChangeText={(value) =>
                            updateContentBlock(index, value)
                          }
                          onRemove={() => {
                            removeContentBlock(index);
                          }}
                        />
                      )}

                      {block.type === "image" && (
                        <ImageBlock
                          blockImage={
                            typeof block.content === "object"
                              ? block.content
                              : null
                          }
                          onOpenBannerImageBlockSelector={(configData) => {
                            onOpenContentBlockImage(configData, index);
                          }}
                          onRemoveBlockImage={() =>
                            updateContentBlock(index, null)
                          }
                          onRemove={() => {
                            removeContentBlock(index);
                          }}
                        />
                      )}
                    </div>
                  ))}
                  <div className="errorMessage">
                    <Message
                      text={
                        errorMessages[
                          ErrorMessage({ fieldName: "content", errors })
                        ] ||  ErrorMessage({ fieldName: "content", errors })
                      }
                      type={"errorMsg"}
                      showMessage={showErrorMessage}
                    />
                  </div>
                  {/* </div> */}
                  <div className="add-block-container">
                    <button
                      type="button"
                      className="add-block-btn"
                      onClick={() => setShowBlockOptions(!showBlockOptions)}
                    >
                      <span className="mdi mdi-plus-circle"></span>
                      Add Content Block
                    </button>

                    {showBlockOptions && (
                      <div className="block-options">
                        <div className="block-options-header">
                          <h4>Choose Content Type</h4>
                        </div>
                        <div className="block-options-list">
                          <button
                            type="button"
                            className="block-option"
                            onClick={() => {
                              addContentBlock("text");
                              setShowBlockOptions(false);
                            }}
                          >
                            <span className="mdi mdi-text"></span>
                            <span>Text Block</span>
                          </button>
                          <button
                            type="button"
                            className="block-option"
                            onClick={() => {
                              addContentBlock("image");
                              setShowBlockOptions(false);
                            }}
                          >
                            <span className="mdi mdi-image"></span>
                            <span>Image Block</span>
                          </button>
                          <button
                            type="button"
                            className="block-option"
                            onClick={() => {
                              addContentBlock("quote");
                              setShowBlockOptions(false);
                            }}
                          >
                            <span className="mdi mdi-format-quote-open"></span>
                            <span>Quote Block</span>
                          </button>
                        </div>
                      </div>
                    )}
                    {/* </div> */}
                  </div>
                  
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card card-default">
            <div className="card-header card-header-border-bottom">
              <h5>Blog Settings</h5>
            </div>
            <div className="card-body">
              <div className="form-group mb-3">
                <label className="form-label mandatory_field">
                  {blog_page_labels?.blog_add_and_edit_page_labels?.status}
                </label>
                <select
                  className="form-control"
                  name="status"
                  value={formValues?.status || ""}
                  onChange={onChangeField}
                >
                  <option value="">Select Status</option>
                  {statusOptions &&
                    statusOptions?.map((optionString, index) => {
                      const [name, value] = optionString.split(",");
                      return (
                        <option value={name} key={index}>
                          {value}
                        </option>
                      );
                    })}
                </select>
                <div className="errorMessage">
                  <Message
                    text={
                      errorMessages[
                        ErrorMessage({ fieldName: "status", errors })
                      ] ||  ErrorMessage({ fieldName: "status", errors })
                    }
                    type={"errorMsg"}
                    showMessage={showErrorMessage}
                  />
                </div>
              </div>
              <div className="form-group mb-3">
                <label className="form-label mandatory_field">
                  {blog_page_labels?.blog_add_and_edit_page_labels?.category}
                </label>
                <select
                  className="form-control"
                  name="blog_category_id"
                  value={formValues?.blog_category_id || ""}
                  onChange={onChangeField}
                >
                  <option value="">Select Category</option>
                  {categories?.map((cat, index) => (
                    <option value={cat.blog_category_id || cat.id} key={index}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                <div className="errorMessage">
                  <Message
                    text={
                      errorMessages[
                        ErrorMessage({ fieldName: "blog_category_id", errors })
                      ] || ErrorMessage({ fieldName: "blog_category_id", errors })
                    }
                    type={"errorMsg"}
                    showMessage={showErrorMessage}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-5">
            <div className="card card-default">
              <div className="card-header card-header-border-bottom">
                <h2>
                  {
                    blog_page_labels?.blog_add_and_edit_page_labels
                      ?.blog_image_block_title
                  }
                </h2>
              </div>
              <div className="card-body">
                <ImagesPreview
                  acceptMultipleFiles={false}
                  onOpen={onOpenFeaturedImage}
                  selectedImageInfo={
                    selectedImages ||
                    (savedImages.length > 0 ? savedImages[0] : null)
                  }
                  onRemoveImageIds={onRemoveImageIds}
                  deleteDisable={true}
                />
                 <div className="errorMessage mt-3">
                                  <Message
                                    text={
                                      errorMessages[
                                        ErrorMessage({ fieldName: "featured_image", errors })
                                      ] || ErrorMessage({ fieldName: "featured_image", errors })
                                    }
                                    type={"errorMsg"}
                                    showMessage={showErrorMessage}
                                  />
                                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ImageSelector
        Config={imageSelector}
        onClose={onClose}
        onSelectImageInfo={onSelectImages}
      />
      <div className=" mt-4">
        <button className="btn btn-primary" onClick={saveBlog}>
          {blog_page_labels?.blog_edit_page_labels?.save_btn_label}
        </button>
      </div>
    </React.Fragment>
  );
}
